import Button from '@mortgage-pos/ui/components/Button'
import Tooltip from '@mortgage-pos/ui/components/Tooltip'
import { Rate } from '@mortgage-pos/types'
import React, { FC } from 'react'
import { rateCodesMap } from '@mortgage-pos/data'
import tooltipCopy from '@mortgage-pos/ui/utils/tooltipCopy'
import { RATE_SUPERLATIVES } from '@mortgage-pos/ui/data/rateSuperlatives'

import { formatNumberToDollarAmount } from '@mortgage-pos/utils'
import { customTextMap } from '@mortgage-pos/ui/utils/customizationHelpers'

interface Props {
  rate: Rate
  superlativeName?: string
  handleSubmitRateClick?: (rate) => void
  modifierClass?: string
  lender?: string
  cta?: string
}

const monthlyPaymentTooltipContent = {
  currentLoan: {
    text: `The monthly payment displayed here may or may not include other
      factors such as lender fees, homeowner's insurance, property taxes,
      homeowner's association dues and private mortgage insurance.`,
  },
  otherOffers: {
    text: `The monthly payment displayed here represents your estimated principal
      and interest payment under the proposed loan terms. Your final calculated
      monthly payment will likely change from this estimate based on other
      factors such as homeowner's insurance, property taxes, homeowner's
      association dues and private mortgage insurance`,
  },
}

const rateOriginTooltipContent = {
  text: `The details of your current loan were pulled from either: (a) the
    mortgage statement you uploaded to ${customTextMap(
      'sageFullPartnerName'
    )} or (b) publicly available
    information associated with your name and home address.`,
}

const RateCardCompare: FC<Props> = ({
  rate,
  superlativeName = 'lowestRate',
  handleSubmitRateClick,
  modifierClass = '',
  lender = '',
  cta = 'Apply for this offer',
}) => {
  const superlative = RATE_SUPERLATIVES[superlativeName] || 'lowestRate'
  const isCurrentLoan = superlativeName === 'currentLoan'
  const paymentTooltipKey = isCurrentLoan ? 'currentLoan' : 'otherOffers'
  const paymentTooltip = monthlyPaymentTooltipContent[paymentTooltipKey]

  return (
    <section className={`rate-card-compare ${modifierClass}`}>
      <div className="rate-card-compare__superlatives-container">
        {superlative.icon ? (
          <img
            src={superlative.icon}
            alt="Recommended offer Icon"
            className="rate-card-compare__superlatives-icon"
          />
        ) : null}

        <span className="rate-card-compare__superlatives-text">
          {superlativeName === 'otherLender' ? lender : superlative.heading}

          {isCurrentLoan ? (
            <Tooltip
              className="rate-card-compare__details-tooltip"
              text={rateOriginTooltipContent.text}
              placement="bottom"
            />
          ) : null}
        </span>
      </div>

      <div className="rate-card-compare__monthly-container">
        <span className="rate-card-compare__monthly-amount">
          {formatDollarAmount(rate.principleAndInterestPayment)}
        </span>

        <span> /mo.</span>

        <Tooltip
          className="rate-card-compare__details-tooltip"
          placement="bottom"
          text={paymentTooltip.text}
        />
      </div>

      <div className="rate-card-compare__term">{rateCodesMap[rate.term]}</div>

      <div className="rate-card-compare__details-container">
        {rate.apr ? (
          <div className="rate-card-compare__rate-container">
            <span>APR</span>
            <Tooltip
              title={tooltipCopy.apr.title}
              text={tooltipCopy.apr.text}
              placement="top"
              className="rate-card-compare__details-tooltip"
            />
            <span className="rate-card-compare__details-value">
              {rate.apr.toFixed(3)}%
            </span>
          </div>
        ) : null}

        <div className="rate-card-compare__rate-container">
          <span>Rate</span>

          <Tooltip
            title={tooltipCopy.rates.title}
            text={tooltipCopy.rates.text}
            placement="top"
            className="rate-card-compare__details-tooltip"
          />

          <span className="rate-card-compare__details-value">
            {rate.rate.toFixed(3)}%
          </span>
        </div>

        {superlativeName !== 'currentLoan' ? (
          <div className="rate-card-compare__points-container">
            <span>Points</span>
            <Tooltip
              title={tooltipCopy.points.title}
              text={tooltipCopy.points.text}
              placement="top"
              className="rate-card-compare__details-tooltip"
            />

            <span className="rate-card-compare__details-value">
              {rate.discountPoints}&nbsp;
            </span>
          </div>
        ) : null}
      </div>

      {superlativeName === 'otherLender' || isCurrentLoan ? null : (
        <>
          <div className="rate-card-compare__lock-button-wrapper">
            <Button
              className="button button--dark rate-card-compare__lock-button"
              content={cta}
              onClick={handleSubmitRateClick}
            />
          </div>
        </>
      )}
    </section>
  )
}

export default RateCardCompare

function formatDollarAmount(number) {
  return formatNumberToDollarAmount(number, 0)
}
